import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, SelectPicker, Checkbox} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { EncyclopediaItem, EncyclopediaTypeEnum, EncyclopediaTypeList, UpsertEncyclopediaItem } from '@/models/encyclopedia-item.model';
import {upsertEncyclopediaItem } from '@/services/encyclopedia-item.service';
import Textarea from '@/components/Textarea';
import { EncyclopediaRelation, UpsertEncyclopediaRelation } from '@/models/encyclopedia-relation.model';
import { upsertEncyclopediaRelation } from '@/services/encyclopedia-relation.service';
import { toastErrorMessage } from '@/utils/toasterMessage';

const runningClubSchema = Schema.Model<UpsertEncyclopediaItem>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  description: Schema.Types.StringType().isRequired('This field is required.'),
  imageUrl: Schema.Types.StringType().isRequired('This field is required.'),
  type: Schema.Types.NumberType().isRequired('This field is required.'),
});

const emptyModel: UpsertEncyclopediaItem = {
  id: undefined,
  name: '',
  description: '',
  imageUrl: undefined,
  type: EncyclopediaTypeEnum.Person,
};

const emptyRelationModel: UpsertEncyclopediaRelation = {
  id: undefined,
  storyChapterId: undefined,
  encyclopediaItemId: undefined,
  unlockAtChapterStart: true
};

interface EncyclopediaItemFormProps{
  formObject?: EncyclopediaItem;
  pageState: PageState
  storyChapterId?: number;
};

const EncyclopediaItemForm: React.FC<EncyclopediaItemFormProps> = ({formObject, pageState, storyChapterId}) => {

  const [formValue, setFormValue] = useState<UpsertEncyclopediaItem>(emptyModel);
  const [encyclopediaRelation, setEncyclopediaRelation] = useState<UpsertEncyclopediaRelation>(emptyRelationModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel});
      return;
    }

    if (formObject) {
      setFormValue(formObject);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  useEffect(() => {
    setEncyclopediaRelation(pre => ({...pre, storyChapterId: storyChapterId}));
  }, [storyChapterId]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<EncyclopediaItem> = await upsertEncyclopediaItem(formValue);
      if (result.success) {
        if(pageState == PageState.New){
          if(storyChapterId){
            const castedModel = {...encyclopediaRelation, encyclopediaItemId: result.result.id};
            const relationRes: BaseResponseModel<EncyclopediaRelation> = await upsertEncyclopediaRelation(castedModel);
            if (!relationRes.success) {
              toastErrorMessage("Encyclopedia item is created, but there is some error on making relation to story chapter");
            }
          }
          nav(`/encyclopedia-items/${result.result.id}`, {state:{tab: 'encyclopedia-item-links'}});
        } else {
          nav(`/encyclopedia-items`);
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='encyclopedia-item-form' fluid 
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={runningClubSchema}
        disabled={loading}
      >
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}} style={{textAlign: 'center'}}>
              <Form.Label>Image</Form.Label>              
              <Form.Control
                accepter={ImageUploader}
                name="imageUrl"
                height="350px"
                url={formValue.imageUrl}
                objectStorageCategory={ObjectStorageFileCategory.EncyclopediaItem}
                onChange={url => setFormValue(pre => ({...pre, imageUrl: url}))}                
              />            
            </Col> 
            <Col span={{xs:24, xl:16}} >
              <Row>
                <Col span={{xs:24, xl:16}}>
                  <Form.Group>
                    <Form.Label>Name </Form.Label>
                    <Form.Control name="name"/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, xl:8}}>
                  <Form.Group>
                    <Form.Label>Type </Form.Label>
                    <Form.Control name="type" accepter={SelectPicker} block
                      data={EncyclopediaTypeList} 
                      searchable={false}
                      placeholder="Select a type"
                    /> 
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control name="description" accepter={Textarea} rows={4} /> 
                  </Form.Group>
                </Col>
              </Row>
              {storyChapterId && <Row>
                <Col span={{xs:24}}>
                    <Checkbox name="unlockAtChapterStart"
                      checked={!!encyclopediaRelation.unlockAtChapterStart}
                      onChange={(val, chk) => setEncyclopediaRelation(pre => ({ ...pre, unlockAtChapterStart: chk }))}
                    >Unlock At Chapter Start</Checkbox>
                </Col>
              </Row>}
                   
            </Col>
          </Row>          
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default EncyclopediaItemForm;

